import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronLeft, FaChevronRight, FaExpandAlt } from "react-icons/fa";
import { GoDot, GoDotFill } from "react-icons/go";
import { HiXMark } from "react-icons/hi2";

export default function ImageSlider({ images }) {
  const [imageIndex, setImageIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const lightboxItem =
    lightboxIndex === null ? null : images[lightboxIndex] ?? null;

  function getNextIndex(index) {
    if (index === images.length - 1) return 0;
    return index + 1;
  }

  function getPrevIndex(index) {
    if (index === 0) return images.length - 1;
    return index - 1;
  }

  function showNextImage() {
    setImageIndex((index) => getNextIndex(index));
  }

  function showPrevImage() {
    setImageIndex((index) => getPrevIndex(index));
  }

  function openLightbox(index) {
    setLightboxIndex(index);
  }

  function closeLightbox() {
    setLightboxIndex(null);
  }

  function showNextLightboxItem() {
    setLightboxIndex((index) => getNextIndex(index));
  }

  function showPrevLightboxItem() {
    setLightboxIndex((index) => getPrevIndex(index));
  }

  useEffect(() => {
    if (lightboxIndex === null) return;

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setLightboxIndex(null);
      }

      if (event.key === "ArrowRight") {
        setLightboxIndex((index) => {
          if (index === null) return null;
          if (index === images.length - 1) return 0;
          return index + 1;
        });
      }

      if (event.key === "ArrowLeft") {
        setLightboxIndex((index) => {
          if (index === null) return null;
          if (index === 0) return images.length - 1;
          return index - 1;
        });
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightboxIndex, images.length]);

  const sliderButtonClass =
    "flex size-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 text-portfolio-text transition-colors duration-200 hover:text-portfolio-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-portfolio-line [&>svg]:h-4 [&>svg]:w-4";

  const lightboxButtonClass =
    "flex size-10 cursor-pointer items-center justify-center rounded-md border border-solid border-white/20 bg-black/45 p-0 text-white transition-colors duration-200 hover:bg-portfolio-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-white [&>svg]:h-5 [&>svg]:w-5";

  const lightboxControlButtonClass =
    "flex size-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 text-white/75 transition-colors duration-200 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white [&>svg]:h-4 [&>svg]:w-4";

  function renderYoutubeMedia(item, className, tabIndex) {
    return (
      <iframe
        className={className}
        src={item.url}
        title={item.alt}
        loading="lazy"
        tabIndex={tabIndex}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <div className="relative h-full w-full">
      <button
        type="button"
        onClick={() => openLightbox(imageIndex)}
        className="absolute left-2 bottom-2 z-10 flex size-8 cursor-pointer items-center justify-center rounded-md border-0 bg-[color-mix(in_srgb,var(--color-card)_70%,transparent)] p-0 text-portfolio-text transition-colors duration-200 hover:bg-portfolio-accent hover:text-portfolio-inverse focus-visible:outline focus-visible:outline-2 focus-visible:outline-portfolio-line [&>svg]:size-4"
        aria-label="Open current project media"
      >
        <FaExpandAlt aria-hidden />
      </button>

      <div className="flex h-full w-full overflow-hidden">
        {images.map((image, index) => {
          const slideStyle = {
            translate: `${-100 * imageIndex}%`,
          };

          if (image.type === "youtube") {
            return (
              <div
                key={image.url}
                className="flex h-full w-full shrink-0 grow-0 items-center justify-center bg-portfolio-deepest [transition:translate_300ms_ease-in-out]"
                style={slideStyle}
                aria-hidden={imageIndex !== index}
              >
                {renderYoutubeMedia(
                  image,
                  "h-full w-full",
                  imageIndex === index ? undefined : -1
                )}
              </div>
            );
          }

          return (
            <button
              key={image.url}
              type="button"
              onClick={() => openLightbox(index)}
              className="block h-full w-full shrink-0 grow-0 cursor-zoom-in appearance-none border-0 bg-transparent p-0 [transition:translate_300ms_ease-in-out] focus-visible:outline focus-visible:outline-2 focus-visible:outline-portfolio-line"
              style={slideStyle}
              aria-label={`Open ${image.alt}`}
              tabIndex={imageIndex === index ? 0 : -1}
            >
              <img
                className="block h-full w-full object-contain"
                src={image.url}
                alt={image.alt}
                aria-hidden={imageIndex !== index}
                loading="lazy"
              />
            </button>
          );
        })}
      </div>

      <div className="absolute bottom-[-40px] left-0 right-0 flex items-center gap-2 px-2 justify-center">
        <button
          onClick={showPrevImage}
          className={sliderButtonClass}
          aria-label="View Previous Image"
        >
          <FaChevronLeft aria-hidden />
        </button>

        <div className="flex items-center gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setImageIndex(index)}
              aria-label={`View media ${index + 1}`}
              className="m-0 cursor-pointer appearance-none border-0 [border-style:none] bg-transparent p-0 pt-1.5 text-portfolio-text hover:text-portfolio-accent [&>svg]:size-5 [&>svg]:fill-current [&>svg]:transition-transform [&>svg]:duration-200 hover:[&>svg]:scale-[1.4] focus-visible:[&>svg]:scale-[1.6] focus-visible:[&>svg]:outline focus-visible:[&>svg]:outline-2 focus-visible:[&>svg]:outline-portfolio-line"
            >
              {index === imageIndex ? (
                <GoDotFill style={{ scale: "1.4" }} aria-hidden />
              ) : (
                <GoDot aria-hidden />
              )}
            </button>
          ))}
        </div>

        <button
          onClick={showNextImage}
          className={sliderButtonClass}
          aria-label="View Next Image"
        >
          <FaChevronRight aria-hidden />
        </button>
      </div>

      {lightboxItem &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Project media viewer"
            onClick={closeLightbox}
          >
            <button
              type="button"
              className={`${lightboxButtonClass} absolute right-4 top-4 z-10`}
              onClick={closeLightbox}
              aria-label="Close media viewer"
            >
              <HiXMark aria-hidden />
            </button>

            <div
              className="flex max-h-[82vh] w-[min(92vw,1400px)] items-center justify-center"
              onClick={(event) => event.stopPropagation()}
            >
              {lightboxItem.type === "youtube" ? (
                <div className="aspect-video w-full max-w-[1200px] overflow-hidden rounded-md bg-black">
                  {renderYoutubeMedia(lightboxItem, "h-full w-full")}
                </div>
              ) : (
                <img
                  className="max-h-[82vh] max-w-full rounded-md object-contain"
                  src={lightboxItem.url}
                  alt={lightboxItem.alt}
                />
              )}
            </div>

            <div
              className="absolute bottom-4 left-1/2 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-md bg-black/45 px-3 py-2 text-xs text-white"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className={lightboxControlButtonClass}
                onClick={showPrevLightboxItem}
                aria-label="View previous media"
              >
                <FaChevronLeft aria-hidden />
              </button>

              <span className="tabular-nums text-nowrap">
                {lightboxIndex + 1} / {images.length}
              </span>
              <div className="flex items-center gap-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setLightboxIndex(index)}
                    aria-label={`View media ${index + 1} in viewer`}
                    className="m-0 cursor-pointer appearance-none border-0 bg-transparent p-0 text-white/70 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white [&>svg]:size-4 [&>svg]:fill-current"
                  >
                    {index === lightboxIndex ? (
                      <GoDotFill aria-hidden />
                    ) : (
                      <GoDot aria-hidden />
                    )}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className={lightboxControlButtonClass}
                onClick={showNextLightboxItem}
                aria-label="View next media"
              >
                <FaChevronRight aria-hidden />
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
